// Настройки вида на этом устройстве (007; ux.md «Табеля», разделы 6 и 11): плотность таблиц.
// Хранится в браузере и применяется сразу, на сервер не уходит. Хранилище может быть закрыто
// (приватное окно, запрет сайта) — тогда действует «Обычная», а выбор живёт до перезагрузки.
import type { TableDensity } from '@kp/ui';
import { useSyncExternalStore } from 'react';

const DENSITY_KEY = 'kp:table-density';
const CHANGE_EVENT = 'kp:view-preferences';

let memoryDensity: TableDensity | null = null;

function readDensity(): TableDensity {
  try {
    const stored = window.localStorage.getItem(DENSITY_KEY);
    if (stored === 'list' || stored === 'dense') return stored;
  } catch {
    // Хранилище закрыто — значение из памяти вкладки.
  }
  return memoryDensity ?? 'list';
}

export function setTableDensity(density: TableDensity): void {
  memoryDensity = density;
  try {
    window.localStorage.setItem(DENSITY_KEY, density);
  } catch {
    // Хранилище закрыто — выбор живёт до перезагрузки вкладки.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  // Выбор в другой вкладке того же браузера.
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Плотность таблиц на этом устройстве: «list» — обычная, «dense» — компактная. */
export function useTableDensityPreference(): TableDensity {
  return useSyncExternalStore(subscribe, readDensity, () => 'list');
}

/** Protocol manager layout is personal on this device; it never changes another user's view. */
export type ManagerLayout = 'list' | 'tiles';
const managerLayouts = new Map<string, ManagerLayout>();
const managerLayoutKey = (userId: string) => `kp:document-manager-layout:${userId}`;
function readManagerLayout(userId: string): ManagerLayout {
  try {
    const stored = window.localStorage.getItem(managerLayoutKey(userId));
    if (stored === 'list' || stored === 'tiles') return stored;
  } catch {
    // The same fallback as table density when local storage is unavailable.
  }
  return managerLayouts.get(userId) ?? 'list';
}
export function setManagerLayout(userId: string, layout: ManagerLayout): void {
  managerLayouts.set(userId, layout);
  try {
    window.localStorage.setItem(managerLayoutKey(userId), layout);
  } catch {
    /* Tab-local fallback. */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}
export function useManagerLayoutPreference(userId: string): ManagerLayout {
  return useSyncExternalStore(
    subscribe,
    () => readManagerLayout(userId),
    () => 'list',
  );
}

/**
 * Панель свойств «Документов» открыта или закрыта — тоже на этом устройстве (замечания владельца
 * 24.10.2026: свойства скрыты, пока их не открыли ⓘ, «Свойства» или Alt+Enter). Без записи —
 * закрыта; память вкладки — только когда хранилище закрыто.
 */
const managerDetails = new Map<string, boolean>();
const managerDetailsKey = (userId: string) => `kp:document-manager-details:${userId}`;
function readManagerDetails(userId: string): boolean {
  try {
    return window.localStorage.getItem(managerDetailsKey(userId)) === 'open';
  } catch {
    return managerDetails.get(userId) ?? false;
  }
}
export function setManagerDetailsOpen(userId: string, open: boolean): void {
  managerDetails.set(userId, open);
  try {
    window.localStorage.setItem(managerDetailsKey(userId), open ? 'open' : 'closed');
  } catch {
    /* Память вкладки. */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}
export function useManagerDetailsPreference(userId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => readManagerDetails(userId),
    () => false,
  );
}
